/* eslint-disable no-case-declarations */
import { Map, fromJS } from 'immutable';
import * as types from 'constants/ActionTypes';
import getSeats from 'utils/purchase/getSeats';
import { preventSelectPetFriendly, saveSeatsOnLS, getUpdatedSeats } from 'utils/seats';

const defaultState = Map({});

/**
 * Whether an incoming action targets a different result pricing context than the cached
 * entry (an absent variant only matches an absent variant). Missing entries never switch.
 * @param {Map|undefined} entry - Cached results seat-map entry.
 * @param {string|undefined} pricingVariant - Requested pricing variant.
 * @returns {boolean} True when the cached entry belongs to another context.
 */
const isPricingContextSwitch = (entry, pricingVariant) =>
  Boolean(entry) && (entry.get('pricingVariant') ?? undefined) !== (pricingVariant ?? undefined);

/**
 * Reducer for handling seat bus state changes.
 * @param {Map} state - The current state of the seat bus.
 * @param {Object} action - The action being dispatched.
 * @returns {Map} The new state of the seat bus.
 */
export default function seatsBus(state = defaultState, action) {
  switch (action.type) {
    case types.SET_SEAT_MAP_TRIPS: {
      const {
        layout,
        diagramType,
        tripSlug,
        way,
        busCategories,
        seatPriceZones = [],
        tripPricingFields,
        passengerTypes = [],
        pricingVariant,
      } = action;
      const entry = state.get(tripSlug);
      // A context switch replaces the entry so no previous-context layout, quote, selected
      // seat, or error survives; a same-context load merges as before and clears its error.
      const contextSwitch = isPricingContextSwitch(entry, pricingVariant);
      const baseState = contextSwitch ? state.set(tripSlug, Map()) : state;
      const clearsError = !contextSwitch && Boolean(entry && entry.get('error'));
      return baseState.mergeIn([tripSlug], {
        layout: fromJS(layout),
        seats: getSeats(action.layout),
        diagramType,
        tripSlug,
        way,
        busCategories,
        seatPriceZones,
        tripPricingFields,
        passengerTypes,
        ...(pricingVariant && { pricingVariant }),
        ...(clearsError && { error: null }),
      });
    }
    case types.RESET_SEAT_TRIPS:
      return defaultState;

    case types.SELECT_SEATS_TRIPS: {
      const { seats: actionSeats, way, tripSlug } = action;
      const seats = preventSelectPetFriendly(actionSeats);
      if (way === 'departs') saveSeatsOnLS({ id: tripSlug, seats });
      return state.set(
        tripSlug,
        Map({
          ...state.get(tripSlug).toJS(),
          selectedSeats: seats,
        }),
      );
    }

    case types.UPDATE_SEAT_TRIPS: {
      // Update the selected seats in the seats state
      const { seat, tripSlug } = action;
      const selectedSeats = state.get(tripSlug).get('selectedSeats');
      const newSelectedSeats = selectedSeats;
      const updatedSeats = getUpdatedSeats({ selectedSeats: newSelectedSeats, seat });
      return state.set(
        action.tripSlug,
        Map({
          ...state.get(action.tripSlug).toJS(),
          selectedSeats: updatedSeats,
        }),
      );
    }

    case types.SET_SEAT_MAP_ERROR: {
      const { tripId, pricingVariant } = action;
      // An error for another context must not leave the previous context's quote visible.
      if (isPricingContextSwitch(state.get(tripId), pricingVariant)) {
        return state.set(tripId, Map({ error: true, ...(pricingVariant && { pricingVariant }) }));
      }
      const erroredState = state.setIn([tripId, 'error'], true);
      return pricingVariant
        ? erroredState.setIn([tripId, 'pricingVariant'], pricingVariant)
        : erroredState;
    }
    default:
      return state;
  }
}
